import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Formula } from '@/types/formula';
import { useFormulaMaterials } from '@/hooks/useFormulaMaterials';
import { DropletIcon, BeakerIcon } from 'lucide-react';

interface FormulaDetailDialogProps {
  formula: Formula | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const FormulaDetailDialog = ({ formula, open, onOpenChange }: FormulaDetailDialogProps) => {
  const { data: materials, isLoading } = useFormulaMaterials(formula?.id || '');

  if (!formula) return null;

  const totalPercentage = materials?.reduce((sum, material) => sum + material.percentage, 0) || 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <DropletIcon className="h-5 w-5 text-primary" />
            <DialogTitle>{formula.name}</DialogTitle>
            <Badge variant="outline">{formula.status}</Badge>
          </div>
          <DialogDescription>
            {formula.description || 'No description provided'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Formula Information */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Formula Information</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Status</p>
                <p className="text-sm">{formula.status}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">Component Count</p>
                <p className="text-sm">{formula.component_count}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">Created</p>
                <p className="text-sm">{new Date(formula.created_at).toLocaleDateString()}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">Last Updated</p>
                <p className="text-sm">{new Date(formula.updated_at).toLocaleDateString()}</p>
              </div>
            </CardContent>
          </Card>

          {/* Materials/Components */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2">
                  <BeakerIcon className="h-5 w-5" />
                  Materials & Components
                </CardTitle>
                {materials && (
                  <Badge variant={totalPercentage === 100 ? "default" : "destructive"}>
                    Total: {totalPercentage.toFixed(1)}%
                  </Badge>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <p className="text-center text-muted-foreground">Loading materials...</p>
              ) : materials && materials.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Material Name</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Percentage</TableHead>
                      <TableHead>CAS Number</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {materials.map((material) => (
                      <TableRow key={material.id}>
                        <TableCell className="font-medium">{material.name}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className="capitalize">
                            {material.type}
                          </Badge>
                        </TableCell>
                        <TableCell>{material.percentage.toFixed(1)}%</TableCell>
                        <TableCell className="font-mono text-sm">
                          {material.cas_number || 'N/A'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <p className="text-center text-muted-foreground">No materials defined</p>
              )}
              
              {materials && totalPercentage !== 100 && (
                <div className="mt-4 p-3 bg-amber-50 dark:bg-amber-950/20 rounded-md">
                  <p className="text-sm text-amber-800 dark:text-amber-200">
                    ⚠️ Total percentage is {totalPercentage.toFixed(1)}%. Formula should total 100%.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </DialogContent>
    </Dialog>
  );
};